import React, { useState, useRef, useEffect } from 'react';
import { useCategorySuggestions } from '../hooks/useCategorySuggestions';

/**
 * Componente de input con sugerencias de categorías
 */
export const CategoryInputWithSuggestions = ({
    value,
    onChange,
    placeholder = "Categoría (opcional)",
    className = "",
    onBlur
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);

    const {
        suggestions,
        loading,
        showSuggestions,
        hideSuggestions,
        selectSuggestion
    } = useCategorySuggestions(value);

    const handleInputChange = (e) => {
        onChange(e.target.value);
    };

    const handleSuggestionClick = (suggestion) => {
        const value = selectSuggestion(suggestion);
        onChange(value);
        // Opcional: cerrar inmediatamente la lista
        hideSuggestions();
        inputRef.current?.focus();
    };

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = (e) => {
        setIsFocused(false);
        // No ocultar inmediatamente para permitir click en sugerencias; el click fuera ya lo oculta
        setTimeout(() => {
            onBlur?.(e);
        }, 0);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            hideSuggestions();
            inputRef.current?.blur();
        }
    };

    // Ocultar sugerencias al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                hideSuggestions();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [hideSuggestions]);

    const shouldShowSuggestions = showSuggestions && suggestions.length > 0;

    return (
        <div className="relative">
            <input
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
            />

            {/* Loading indicator */}
            {loading && isFocused && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                </div>
            )}

            {/* Suggestions dropdown */}
            {shouldShowSuggestions && (
                <div
                    ref={suggestionsRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                >
                    {suggestions.map((suggestion, index) => (
                        <button
                            key={index}
                            type="button"
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none first:rounded-t-lg last:rounded-b-lg"
                        >
                            <span className="text-gray-800">{suggestion}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
